import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    hoursMinutesSecondsSince,
    lessThanAnHourAgo,
    diffMoreThan24Hours,
} from '../shared/utilities.js';
import { useLocalStorageState } from '../shared/db.js';
import {
    DEFAULT_PRESETS,
    DOSES_STORAGE_KEY,
    PRESETS_STORAGE_KEY,
} from '../shared/constants.js';
import EmptyState from '../components/entries/EmptyState.js';
import DoseEntry from '../components/entries/DoseEntry.js';
import DoseMarker from '../components/entries/DoseMarker.js';
import DayBreak from '../components/entries/DayBreak.js';
import TimeDiff from '../components/entries/TimeDiff.js';
import BeenSince from '../components/BeenSince.js';
import AddNewDoseModal from '../components/AddNewDoseModal.js';
import EditDoseModal from '../components/EditDoseModal.js';
import TooSoonWarningModal from '../components/TooSoonWarningModal.js';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal.js';
import ClearDataModal from '../components/ClearDataModal.js';
import ChangePresetsModal from '../components/ChangePresetsModal.js';
import OptionsMenu from '../components/OptionsMenu.js';
import MenuIcon from '../components/MenuIcon.js';
import ArrowDownIcon from '../components/ArrowDownIcon.js';
import AddDoseFab from '../components/AddDoseFab.js';
import Logo from '../assets/doser.svg';
import { useLanguage } from '../context/LanguageContext.js';
import { usei18n } from '../i18n/i18n.js';
import './Home.css';

const Home = () => {
    const { language } = useLanguage();
    const i18n = usei18n(language);
    const [doses, setDoses] = useLocalStorageState(DOSES_STORAGE_KEY, []);
    const [modalOpen, setModalOpen] = useState(false);
    const [warningOpen, setWarningOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [activeEntry, setActiveEntry] = useState(null);
    const [clearDataOpen, setClearDataOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [presets, setPresets] = useLocalStorageState(PRESETS_STORAGE_KEY, DEFAULT_PRESETS);
    const [presetsOpen, setPresetsOpen] = useState(false);
    const [markTrigger, setMarkTrigger] = useState(0);
    const [, setStaleTick] = useState(0);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const lastDoseTimestamp = doses.length ? doses[doses.length - 1].timestamp : null;
    const entryRefs = useRef(new Map());
    const entryRectsRef = useRef(new Map());
    const hasAnimatedRef = useRef(false);

    useEffect(() => {
        const interval = setInterval(() => setStaleTick(t => t + 1), 60000);
        return () => clearInterval(interval);
    }, []);

    const addMarker = (dose) => {
        const updated = doses.map(d => d.timestamp === dose.timestamp
            ? { ...d, markers: [...(d.markers || []), (new Date()).toISOString()] }
            : d
        );
        setDoses(updated);
        setMarkTrigger(t => t + 1);
    }

    const entries = doses.map((dose, index) => {
        const previous = index > 0 ? doses[index - 1].timestamp : null
        const current = dose.timestamp
        const longDiff = diffMoreThan24Hours(previous, current)
        const [hoursOld] = hoursMinutesSecondsSince(current)
        const isMuted = hoursOld >= 24
        const isLatest = dose.timestamp === lastDoseTimestamp && hoursOld < 24
        return (
            <div
                className={`entry-group ${isMuted ? 'muted' : ''}`}
                key={dose.timestamp}
                ref={(el) => {
                    if (el) entryRefs.current.set(dose.timestamp, el);
                    else entryRefs.current.delete(dose.timestamp);
                }}
            >
                {previous && !longDiff ? <TimeDiff previous={previous} current={current} latest={isLatest} /> : null}
                {!previous || longDiff ? <DayBreak timestamp={current} first={!previous} /> : null}
                <DoseEntry
                    dose={dose}
                    active={activeEntry === dose.timestamp}
                    latest={isLatest}
                    deletable={!isMuted}
                    onToggle={() => setActiveEntry(activeEntry === dose.timestamp ? null : dose.timestamp)}
                    onEdit={(d) => { setActiveEntry(null); setEditTarget(d); }}
                    onDelete={(d) => { setActiveEntry(null); setDeleteTarget(d); }}
                    onMark={(d) => { addMarker(d); setActiveEntry(null); }}
                />
                {(dose.markers || []).map((markerTimestamp) => (
                    <DoseMarker key={markerTimestamp} timestamp={markerTimestamp} />
                ))}
            </div>
        )
    });

    useLayoutEffect(() => {
        if (lastDoseTimestamp) {
            const objDiv = document.getElementById('entries');
            objDiv.scrollTop = objDiv.scrollHeight;
        }
    }, [lastDoseTimestamp, markTrigger])

    const updateScrollToBottom = useCallback(() => {
        const container = document.getElementById('entries');
        const latestEl = lastDoseTimestamp ? entryRefs.current.get(lastDoseTimestamp) : null;
        if (!container || !latestEl || container.scrollHeight <= container.clientHeight) {
            setShowScrollToBottom(false);
            return;
        }
        const latestTop = latestEl.getBoundingClientRect().top;
        setShowScrollToBottom(latestTop >= container.getBoundingClientRect().bottom);
    }, [lastDoseTimestamp]);

    useEffect(() => {
        const container = document.getElementById('entries');
        updateScrollToBottom();
        container.addEventListener('scroll', updateScrollToBottom, { passive: true });
        window.addEventListener('resize', updateScrollToBottom);
        return () => {
            container.removeEventListener('scroll', updateScrollToBottom);
            window.removeEventListener('resize', updateScrollToBottom);
        };
    }, [updateScrollToBottom, doses]);

    const scrollToBottom = () => {
        const container = document.getElementById('entries');
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }

    useLayoutEffect(() => {
        const previousRects = entryRectsRef.current;
        const nextRects = new Map();
        entryRefs.current.forEach((el, timestamp) => {
            nextRects.set(timestamp, el.getBoundingClientRect());
        });

        if (hasAnimatedRef.current) {
            entryRefs.current.forEach((el, timestamp) => {
                const nextRect = nextRects.get(timestamp);
                const previousRect = previousRects.get(timestamp);
                el.style.transition = 'none';
                if (previousRect) {
                    const deltaY = previousRect.top - nextRect.top;
                    el.style.transform = deltaY ? `translateY(${deltaY}px)` : '';
                } else {
                    el.style.transform = 'translateY(2.5rem)';
                    el.style.opacity = '0';
                }
            });

            requestAnimationFrame(() => {
                entryRefs.current.forEach((el) => {
                    el.style.transition = 'transform 0.35s ease, opacity 0.35s ease';
                    el.style.transform = '';
                    el.style.opacity = '';
                });
            });
        }

        hasAnimatedRef.current = true;
        entryRectsRef.current = nextRects;
    }, [doses])

    const handleAddDoseClick = () => {
        if (lastDoseTimestamp && lessThanAnHourAgo(lastDoseTimestamp)) {
            setWarningOpen(true);
        } else {
            setModalOpen(true);
        }
    }

    const addNewDose = (doseSize) => {
        const newDose = { timestamp: (new Date()).toISOString(), size: doseSize };
        setDoses([...doses, newDose]);
        setModalOpen(false);
    }

    const addLetterEntry = (letter) => {
        const newDose = { timestamp: (new Date()).toISOString(), letter };
        setDoses([...doses, newDose]);
    }

    const deleteDose = () => {
        setDoses(doses.filter(d => d.timestamp !== deleteTarget.timestamp));
        setDeleteTarget(null);
    }

    const editDose = (updatedDose) => {
        const updated = doses
            .filter(d => d.timestamp !== editTarget.timestamp)
            .concat([updatedDose])
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        setDoses(updated);
        setEditTarget(null);
    }

    const clearAllData = () => {
        setDoses([]);
        setClearDataOpen(false);
    }

    const savePresets = (newPresets) => {
        setPresets(newPresets);
        setPresetsOpen(false);
    }

    return (
        <section id="doses">
            <header className="header">
                <img src={Logo} className="logo" alt="" />
                <h1>{i18n('appTitle')}</h1>
            </header>
            {lastDoseTimestamp ? (
                <button onClick={() => setMenuOpen(true)} className="options-button" aria-label="Options">
                    <MenuIcon />
                </button>
            ) : null}
            {showScrollToBottom ? (
                <button onClick={scrollToBottom} className="scroll-to-bottom-button" aria-label="Scroll to latest dose">
                    <ArrowDownIcon />
                </button>
            ) : null}
            <div id="entries" className="entries" onClick={() => setActiveEntry(null)}>{entries.length ? entries : <EmptyState onAdd={handleAddDoseClick} onAddLetter={addLetterEntry} />}</div>
            <footer className="footer">
                {lastDoseTimestamp ? <BeenSince timestamp={lastDoseTimestamp} /> : null}
            </footer>
            {lastDoseTimestamp ? (
                <AddDoseFab onAddDose={handleAddDoseClick} onAddLetter={addLetterEntry} />
            ) : null}
            {warningOpen ? <TooSoonWarningModal timeSince={hoursMinutesSecondsSince(lastDoseTimestamp)} onProceed={() => { setWarningOpen(false); setModalOpen(true); }} onClose={() => setWarningOpen(false)} /> : null}
            {modalOpen ? <AddNewDoseModal onSave={addNewDose} onClose={() => setModalOpen(false)} /> : null}
            {deleteTarget ? <DeleteConfirmationModal dose={deleteTarget} onConfirm={deleteDose} onClose={() => setDeleteTarget(null)} /> : null}
            {editTarget ? (
                <EditDoseModal
                    dose={editTarget}
                    previousTimestamp={doses[doses.findIndex(d => d.timestamp === editTarget.timestamp) - 1]?.timestamp}
                    onSave={editDose}
                    onClose={() => setEditTarget(null)}
                />
            ) : null}
            {clearDataOpen ? <ClearDataModal onConfirm={clearAllData} onClose={() => setClearDataOpen(false)} /> : null}
            {menuOpen ? (
                <OptionsMenu
                    onRemoveAllData={() => { setMenuOpen(false); setClearDataOpen(true); }}
                    onConfigurePresets={() => { setMenuOpen(false); setPresetsOpen(true); }}
                    onClose={() => setMenuOpen(false)}
                />
            ) : null}
            {presetsOpen ? <ChangePresetsModal presets={presets} onSave={savePresets} onClose={() => setPresetsOpen(false)} /> : null}
        </section>
    )
}

export default Home;
